import type { TourStep } from '@/components/ui/Tour';
import { PAYMENTS_MANAGEMENT_TOUR_STEP_IDS, TOUR_CENTERED_STEP_SELECTOR } from '../tour-constants';
import { TourStepContent } from './StepContent';

export type PaymentsManualCollectionContext = 'academy_starter' | 'club_before_connect';

export interface PaymentsSettingsTourContext {
  isStarterPlan: boolean;
  manualCollectionContext?: PaymentsManualCollectionContext;
}

function resolveManualCollectionContext(
  ctx: PaymentsSettingsTourContext,
): PaymentsManualCollectionContext {
  if (ctx.manualCollectionContext) {
    return ctx.manualCollectionContext;
  }
  return ctx.isStarterPlan ? 'academy_starter' : 'club_before_connect';
}

function paymentsPageIntro(ctx: PaymentsSettingsTourContext): TourStep {
  const manualCollectionContext = resolveManualCollectionContext(ctx);

  if (manualCollectionContext === 'club_before_connect') {
    return {
      content: (
        <TourStepContent
          title="Before Stripe Connect is active"
          description="Your club subscription supports online payments once Stripe Connect is set up. Until then, use the billing report to reconcile court hire, membership, and play event fees collected manually."
        />
      ),
      selectorId: TOUR_CENTERED_STEP_SELECTOR,
    };
  }

  if (ctx.isStarterPlan) {
    return {
      content: (
        <TourStepContent
          title="Payments on Starter"
          description="CourtSync does not process lesson checkout on Starter. Use this area for payment instructions families see when booking, and the billing report to reconcile attendance against list prices."
        />
      ),
      selectorId: TOUR_CENTERED_STEP_SELECTOR,
    };
  }

  return {
    content: (
      <TourStepContent
        title="Payments and payouts"
        description="Connect Stripe, review charges and payouts, configure tax and discounts, and sync lesson revenue to accounting when your plan includes it."
      />
    ),
    selectorId: TOUR_CENTERED_STEP_SELECTOR,
  };
}

function professionalTabsIntro(): TourStep {
  return {
    content: (
      <TourStepContent
        title="Payment tabs"
        description="Move between Dashboard, Settings, Tax, Discounts, Promo codes, and Accounting integrations depending on what you need to configure."
      />
    ),
    selectorId: PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.TABS,
    position: 'bottom',
  };
}

function withPaymentsPageIntro(
  steps: TourStep[],
  ctx: PaymentsSettingsTourContext,
): TourStep[] {
  return [paymentsPageIntro(ctx), ...steps];
}

export interface PaymentsStarterTourOptions {
  onEnterBillingReport?: () => void;
  onEnterSettings?: () => void;
  manualCollectionContext?: PaymentsManualCollectionContext;
}

function billingReportStep(
  options: PaymentsStarterTourOptions,
  manualCollectionContext: PaymentsManualCollectionContext,
): TourStep {
  return {
    content: (
      <TourStepContent
        title="Billing report"
        description={
          manualCollectionContext === 'club_before_connect'
            ? 'Export court hire, membership, play event, and other fee rows by period. Use the CSV while you collect payments outside CourtSync.'
            : 'Export attendance against list prices by month, term, or lesson. Use this CSV to chase manual payments while you are on Starter without Stripe Connect.'
        }
      />
    ),
    selectorId: PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.BILLING_REPORT,
    position: 'top',
    onStepEnter: options.onEnterBillingReport,
  };
}

function settingsStep(
  options: PaymentsStarterTourOptions,
  manualCollectionContext: PaymentsManualCollectionContext,
): TourStep {
  return {
    content: (
      <TourStepContent
        title={
          manualCollectionContext === 'club_before_connect'
            ? 'Stripe Connect setup'
            : 'Payment instructions'
        }
        description={
          manualCollectionContext === 'club_before_connect'
            ? 'Open Settings to connect Stripe and turn on online payments for court hire, memberships, and guest checkout.'
            : 'Add bank transfer, cash, or other collection details. Families see these instructions when they request a booking on your public site.'
        }
      />
    ),
    selectorId: PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.SETTINGS,
    position: 'top',
    onStepEnter: options.onEnterSettings,
  };
}

export function getPaymentsStarterTourSteps(
  options: PaymentsStarterTourOptions = {},
): TourStep[] {
  const manualCollectionContext =
    options.manualCollectionContext ??
    ('academy_starter' satisfies PaymentsManualCollectionContext);

  return [
    paymentsPageIntro({ isStarterPlan: true, manualCollectionContext }),
    billingReportStep(options, manualCollectionContext),
    settingsStep(options, manualCollectionContext),
  ];
}

export function getPaymentsSettingsTourSteps(ctx: PaymentsSettingsTourContext): TourStep[] {
  if (ctx.isStarterPlan || ctx.manualCollectionContext === 'club_before_connect') {
    return getPaymentsStarterTourSteps({
      manualCollectionContext: resolveManualCollectionContext(ctx),
    });
  }

  return withPaymentsPageIntro(
    [
      {
        content: (
          <TourStepContent
            title="Stripe Connect and receipts"
            description="Complete Stripe onboarding, connect your payout account, and configure receipt notes and surcharge options for online lesson checkout."
          />
        ),
        selectorId: PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.SETTINGS,
        position: 'top',
      },
    ],
    ctx,
  );
}

export const paymentsBillingReportTourSteps: TourStep[] = getPaymentsStarterTourSteps();

export const paymentsStarterTourSteps: TourStep[] = getPaymentsStarterTourSteps();

export const paymentsDashboardTourSteps: TourStep[] = withPaymentsPageIntro(
  [
    professionalTabsIntro(),
    {
    content: (
      <TourStepContent
        title="Payments and payouts"
        description="Review Stripe charges and transfers from your connected account. Switch between Payments and Payouts to reconcile what families paid and what landed in your bank."
      />
    ),
    selectorId: PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.DASHBOARD,
    position: 'top',
  },
  ],
  { isStarterPlan: false },
);

/** Default registry steps for anchor readiness checks. */
export const paymentsSettingsTourSteps: TourStep[] = getPaymentsSettingsTourSteps({
  isStarterPlan: false,
});

export const paymentsDiscountsTourSteps: TourStep[] = withPaymentsPageIntro(
  [
    {
      content: (
        <TourStepContent
          title="Discounts"
          description="Configure sibling discounts and membership-linked lesson discounts. Eligible families receive the reduction automatically at online checkout."
        />
      ),
      selectorId: PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.DISCOUNTS,
      position: 'top',
    },
  ],
  { isStarterPlan: false },
);

export const paymentsPromoCodesTourSteps: TourStep[] = withPaymentsPageIntro(
  [
    {
      content: (
        <TourStepContent
          title="Promo codes"
          description="Create percent or fixed-amount codes with optional validity windows and per-user redemption limits for lesson checkout."
        />
      ),
      selectorId: PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.PROMO_CODES,
      position: 'top',
    },
  ],
  { isStarterPlan: false },
);

